import React from 'react';

import moment from 'moment';
import { connect } from 'react-redux';

import { withWidth } from '@material-ui/core';
import { isWidthDown, WithWidth } from '@material-ui/core/withWidth';

import { userSelector } from '../../reducers/current-user-selectors';
import {
  calendarEventFilters,
  hideCalendarDisabledEvents,
} from '../../reducers/display-settings-selectors';

import { CriticalDateEventPill } from './critical-date-event-pill';

import { LoadingSkeleton } from '../../widgets/loading-skeleton';
import { CalendarDisplayType } from '../utils/calendar-display-type';

type InputProps = {
  orgId: number;
  dateString: string;
  criticalDates: Array<CriticalDate> | undefined;
  calendarDisplay: ValueOf<typeof CalendarDisplayType>;
  className?: string;
};

interface StateProps {
  eventFilters: Array<SelectOption<string>>;
  hideDisabledEvents: boolean;
}
const mapStateToProps = (state: any): StateProps => {
  const user = userSelector(state);
  return {
    hideDisabledEvents: hideCalendarDisabledEvents(state, user.username),
    eventFilters: calendarEventFilters(state, user.username),
  };
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type Props = InputProps & ReduxProps & WithWidth;

export const EventDayCard = withWidth()(
  connect(mapStateToProps)(
    ({
      orgId,
      dateString,
      criticalDates,
      hideDisabledEvents,
      eventFilters,
      calendarDisplay,
      className,
      width,
    }: Props) => {
      const isToday = moment().isSame(moment(dateString), 'day');
      const onMobile = isWidthDown('sm', width);

      const criticalDateKey = (event: CriticalDate) => {
        const labelPrefix = `${event.eventType}-${event.startAt}-${event.title}`;

        return event.jobApplicantId
          ? `${labelPrefix}-${event.jobApplicantId}`
          : `${labelPrefix}-${event.triggeredCommunicationId}-${event.employeeId}`;
      };

      const filteredCriticalDatesForDate = (criticalDates || []).filter((criticalDate) => {
        const hiddenInactive = hideDisabledEvents && !criticalDate.communicationEnabled;
        const selectedEventType = eventFilters.some(
          (opt) => opt.selected && new RegExp(opt.value).test(criticalDate.eventType),
        );
        return !hiddenInactive && selectedEventType;
      });

      const DateLabel = () => {
        const date = moment(dateString);

        if (calendarDisplay === CalendarDisplayType.MONTH) {
          return (
            <div
              className={`u-alignSelf-center u-textAlign-center u-padding-sm ${
                isToday ? 'u-width-30px u-background-secondary u-borderRadius-50 u-color-white' : ''
              }`}
            >
              {date.format('D')}
            </div>
          );
        }

        let day;
        if (!onMobile && moment().subtract(1, 'day').isSame(date, 'day')) {
          day = 'Yesterday';
        } else if (isToday) {
          day = 'Today';
        } else if (!onMobile && moment().add(1, 'day').isSame(date, 'day')) {
          day = 'Tomorrow';
        } else {
          day = date.format('ddd');
        }
        return (
          <div className="u-alignSelf-center u-textAlign-center u-padding-sm">
            {`${day} ${date.format('M/D')}`}
          </div>
        );
      };

      const NoEventsForDate = () =>
        calendarDisplay === CalendarDisplayType.MONTH ? (
          <div className="u-display-flex u-justifyContent-center u-alignItems-center u-height-50px" />
        ) : (
          <div className="u-display-flex u-justifyContent-center u-alignItems-center u-minHeight-100px">
            <div className="typo-subtitle u-color-gray">No Events</div>
          </div>
        );

      return (
        <div
          key={dateString}
          className={`u-display-flex u-flexDirection-column u-flex-1 u-padding-xs ${
            calendarDisplay === CalendarDisplayType.MONTH ? 'u-height-100' : 'u-height-200px'
          } ${className}`}
        >
          <DateLabel />
          <div className="u-overflow-auto">
            {!criticalDates ? (
              <LoadingSkeleton lines={3} />
            ) : filteredCriticalDatesForDate.length === 0 ? (
              <NoEventsForDate />
            ) : (
              filteredCriticalDatesForDate.map((event) => (
                <CriticalDateEventPill
                  key={criticalDateKey(event)}
                  orgId={orgId}
                  criticalDate={event}
                />
              ))
            )}
          </div>
        </div>
      );
    },
  ),
);
